import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, Eye, X } from "lucide-react";
import { ReactComponent as ChairIcon } from "../assets/chair.svg";
import { ReactComponent as PotionArabesque } from "../assets/arabesquepotion.svg";
import Arabesque from "./Arabesque";
import { useAuth } from "./AuthGate";
import ToggleSwitch from "./ToggleSwitch";
import MenuIconTooltip from "./MenuIconTooltip";
import VocabInfoTip from "./VocabInfoTip";
import { getBlasonByIndex, getRoturierBlason } from "../constants/radiantDayBlasons";
import { readRadiantDayDisplayedPortrait } from "../utils/radiantDayTitlesPreference";
import { FRAME_LAUNCHER_TOOLTIPS } from "../constants/menuTooltips";
import { TEXT_SCALE_FRAME_GROW, useAccountTextScale } from "../constants/textScalePreference";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import {
  PRESENCE_CHANGED_EVENT,
  fetchCitizens,
  postMessagingAppearance,
  saveCitizenCard,
} from "../utils/kingdomMessaging";
import {
  CITIZEN_LINE_MAX,
  CITIZEN_PRESENTATION_LINES,
  CITIZEN_PRESENTATION_MAX,
} from "../multiuser/citizenCard";
import "./DeleteWordModal.css";
import "./KingdomCitizens.css";

const EMPTY_CARD = {
  favoriteNovel: "",
  inspiringCharacter: "",
  currentReading: "",
  presentation: "",
};

const LINE_TIP = `Optionnel. ${CITIZEN_LINE_MAX} caractères au maximum.`;
const PRESENTATION_TIP = `Optionnel. ${CITIZEN_PRESENTATION_MAX} caractères au maximum, sur ${CITIZEN_PRESENTATION_LINES} lignes.`;
const PRESENTATION_FALLBACK = "Brave citoyen du Royaume";
const NOVEL_LABEL = "Un de mes romans préféré est:";
const CHARACTER_LABEL = "Un personnage romanesque qui m'inspire:";
const READING_LABEL = "En ce moment, je lis:";
const PRESENTATION_LABEL = "Quelques mots pour me présenter:";
const TIP_Z_INDEX = 2147483002;

function limitPresentation(raw) {
  return String(raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .slice(0, CITIZEN_PRESENTATION_LINES)
    .join("\n")
    .slice(0, CITIZEN_PRESENTATION_MAX);
}

function citizensWindowStyle(box, frameGrow = 0, extraWidth = 0) {
  const scale = box.scale > 0 ? box.scale : 1;
  const frameWidth = box.width > 0 ? box.width / scale : 1300;
  const frameHeight = box.height > 0 ? box.height / scale : 860;
  const grow = frameGrow > 0 ? frameGrow / scale : 0;
  const designWidth =
    Math.min(680, Math.max(360, frameWidth - 96)) + grow + (150 + extraWidth) / scale;
  const designHeight = Math.min(760, Math.max(440, frameHeight - 80)) + grow;
  const visualWidth = designWidth * scale;
  const visualHeight = designHeight * scale;
  const left = box.width > 0 ? box.left + (box.width - visualWidth) / 2 : 16;
  const top = box.height > 0 ? box.top + (box.height - visualHeight) / 2 : 16;
  return {
    width: `${designWidth}px`,
    height: `${designHeight}px`,
    left: `${left}px`,
    top: `${top}px`,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
  };
}

function readFrameBox() {
  const frame = document.querySelector(".custom-pattern");
  if (!frame) return { left: 0, top: 0, width: 0, height: 0, scale: 1 };
  const rect = frame.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    scale: getAppScale(),
  };
}

function displayedBlasonIndex(playerName) {
  const cached = readRadiantDayDisplayedPortrait(playerName || "");
  const index = Number(cached?.blasonIndex);
  return Number.isInteger(index) && index >= 0 ? index : null;
}

function blasonSrc(index) {
  return (getBlasonByIndex(index) || getRoturierBlason())?.imageSrc || "";
}

function citizenBlasonIndex(citizen, selfName) {
  if (citizen?.self) {
    const local = displayedBlasonIndex(selfName);
    if (local != null) return local;
  }
  const index = Number(citizen?.blasonIndex);
  return Number.isInteger(index) && index >= 0 ? index : 0;
}

function CitizensScrollbar({ trackRef, scrollbar, onTrackPointerDown, onThumbPointerDown }) {
  return (
    <div
      ref={trackRef}
      className={`kingdom-citizens__scrollbar${
        scrollbar.visible ? "" : " kingdom-citizens__scrollbar--hidden"
      }`}
      aria-hidden="true"
      onPointerDown={onTrackPointerDown}
    >
      {scrollbar.visible ? (
        <div
          className="kingdom-citizens__scrollbar-thumb"
          style={{
            height: `${scrollbar.height}px`,
            transform: `translateY(${scrollbar.top}px)`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

function LiteraryLine({ label, tip, value, editing, field, draft, onDraft, multiline = false }) {
  const maxLength = multiline ? CITIZEN_PRESENTATION_MAX : CITIZEN_LINE_MAX;
  return (
    <label className="kingdom-citizens__field">
      <span className="kingdom-citizens__field-head">
        {editing ? <VocabInfoTip tooltip={tip} ariaLabel={tip} zIndex={TIP_Z_INDEX} /> : null}
        <span className="kingdom-citizens__field-label">{label}</span>
      </span>
      {editing ? (
        multiline ? (
          <textarea
            className="kingdom-citizens__input kingdom-citizens__input--presentation"
            value={draft[field]}
            rows={CITIZEN_PRESENTATION_LINES}
            maxLength={maxLength}
            onChange={(event) => onDraft(field, limitPresentation(event.target.value))}
          />
        ) : (
          <input
            className="kingdom-citizens__input"
            value={draft[field]}
            maxLength={maxLength}
            onChange={(event) => onDraft(field, event.target.value.slice(0, maxLength))}
          />
        )
      ) : (
        <p
          className={`${multiline ? "kingdom-citizens__presentation-text" : ""}${
            value ? "" : " kingdom-citizens__empty-value"
          }`}
        >
          {value || "Non renseigné"}
        </p>
      )}
    </label>
  );
}

function LiteraryQuote({ text }) {
  const value = String(text || "").trim() ? text : PRESENTATION_FALLBACK;
  return (
    <p className="kingdom-citizens__quote">
      <span className="kingdom-citizens__quote-mark" aria-hidden="true">
        “
      </span>
      {value}
      <span className="kingdom-citizens__quote-mark" aria-hidden="true">
        ”
      </span>
    </p>
  );
}

function CitizensDivider() {
  return <PotionArabesque className="kingdom-citizens__divider" aria-hidden="true" />;
}

const DIRECTORY_SKELETON_ONLINE = ["Royn"];
const DIRECTORY_SKELETON_OFFLINE = [
  "Anatole",
  "Gwynplaine",
  "La fille Adams",
  "Pumblechook",
  "Queequeg",
  "Raskolnikov",
];

function DirectorySkeletonGroup({ title, names, blasonSrc }) {
  return (
    <section className="kingdom-citizens__group">
      <h3 className="kingdom-citizens__group-title">
        <span>
          <span className="kingdom-citizens__skel-label">{title}</span>
        </span>
        <span className="kingdom-citizens__dot kingdom-citizens__skel-dot" />
      </h3>
      <ul className="kingdom-citizens__list">
        {names.map((name) => (
          <li key={name} className="kingdom-citizens__row">
            <img
              className="kingdom-citizens__blason kingdom-citizens__skel-blur"
              src={blasonSrc}
              alt=""
            />
            <span className="kingdom-citizens__name">
              <span className="kingdom-citizens__skel-name">{name}</span>
            </span>
            <span className="kingdom-citizens__eye kingdom-citizens__skel-eye" />
          </li>
        ))}
      </ul>
    </section>
  );
}

function DirectorySkeleton() {
  const blasonSrc = getRoturierBlason()?.imageSrc || "";
  return (
    <div className="kingdom-citizens__skel" aria-hidden="true">
      <DirectorySkeletonGroup
        title="Membre actuellement connecté (1)"
        names={DIRECTORY_SKELETON_ONLINE}
        blasonSrc={blasonSrc}
      />
      <PotionArabesque
        className="kingdom-citizens__divider kingdom-citizens__skel-blur"
        aria-hidden="true"
      />
      <DirectorySkeletonGroup
        title="Membres hors ligne (6)"
        names={DIRECTORY_SKELETON_OFFLINE}
        blasonSrc={blasonSrc}
      />
    </div>
  );
}

export default function KingdomCitizens({ reading = false }) {
  const { user } = useAuth();
  const [textScale] = useAccountTextScale(user?.sub || "");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("directory");
  const [frameBox, setFrameBox] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    scale: 1,
  });
  const [citizens, setCitizens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewedId, setViewedId] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState("");
  const [draft, setDraft] = useState(EMPTY_CARD);
  const listRef = useRef(null);

  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(listRef);

  useLayoutEffect(() => {
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer =
      frame && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  const loadCitizens = useCallback(async () => {
    const json = await fetchCitizens();
    setCitizens(Array.isArray(json.citizens) ? json.citizens : []);
    setError("");
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    const localBlason = displayedBlasonIndex(user?.displayName);
    const publish =
      localBlason == null
        ? Promise.resolve()
        : postMessagingAppearance(localBlason).catch(() => {});
    publish
      .then(() => loadCitizens())
      .catch((err) => {
        if (!cancelled) setError(err.message || "Impossible de charger l'annuaire.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    const timer = window.setInterval(() => {
      loadCitizens().catch(() => {});
    }, 15000);
    const onPresence = () => {
      loadCitizens().catch(() => {});
    };
    window.addEventListener(PRESENCE_CHANGED_EVENT, onPresence);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(PRESENCE_CHANGED_EVENT, onPresence);
    };
  }, [open, loadCitizens, user?.displayName]);

  useEffect(() => {
    if (editing) return;
    const me = citizens.find((citizen) => citizen.self);
    if (!me) return;
    setDraft({
      favoriteNovel: me.favoriteNovel || "",
      inspiringCharacter: me.inspiringCharacter || "",
      currentReading: me.currentReading || "",
      presentation: me.presentation || "",
    });
  }, [citizens, editing]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useLayoutEffect(() => {
    updateScrollbar();
  }, [citizens, viewedId, tab, loading, open, updateScrollbar]);

  if (!user?.sub) return null;
  const portalRoot = getAppPortalRoot();
  if (!portalRoot) return null;

  const scale = frameBox.scale || 1;
  const toggleStyle = {
    left: `${frameBox.left}px`,
    top: `${frameBox.top + frameBox.height / 2 + (reading ? 117 : -39) * scale}px`,
  };
  const selfName = user.displayName || "";
  const me = citizens.find((citizen) => citizen.self) || null;
  const viewed = citizens.find((citizen) => citizen.userId === viewedId) || null;
  const online = citizens.filter((citizen) => citizen.online);
  const offline = citizens.filter((citizen) => !citizen.online);

  const close = () => {
    if (saving) return;
    setOpen(false);
    setViewedId("");
    setEditing(false);
    setCardError("");
    setTab("directory");
  };

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const finishEditing = async (next) => {
    if (next) {
      setCardError("");
      setEditing(true);
      return;
    }
    const unchanged =
      me &&
      (me.favoriteNovel || "") === draft.favoriteNovel &&
      (me.inspiringCharacter || "") === draft.inspiringCharacter &&
      (me.currentReading || "") === draft.currentReading &&
      (me.presentation || "") === draft.presentation;
    if (unchanged) {
      setEditing(false);
      setCardError("");
      return;
    }
    setSaving(true);
    setCardError("");
    try {
      const saved = await saveCitizenCard(draft);
      const nextCard = {
        favoriteNovel: saved?.favoriteNovel ?? draft.favoriteNovel,
        inspiringCharacter: saved?.inspiringCharacter ?? draft.inspiringCharacter,
        currentReading: saved?.currentReading ?? draft.currentReading,
        presentation: saved?.presentation ?? draft.presentation,
      };
      setCitizens((current) =>
        current.map((citizen) => (citizen.self ? { ...citizen, ...nextCard } : citizen))
      );
      setDraft(nextCard);
      setEditing(false);
    } catch (err) {
      setCardError(err.message || "Impossible d'enregistrer la carte.");
    } finally {
      setSaving(false);
    }
  };

  const renderCitizenRow = (citizen) => {
    const index = citizenBlasonIndex(citizen, selfName);
    return (
      <li key={citizen.userId} className="kingdom-citizens__row">
        <img className="kingdom-citizens__blason" src={blasonSrc(index)} alt="" />
        <span className="kingdom-citizens__name">{citizen.displayName}</span>
        <button
          type="button"
          className="kingdom-citizens__eye"
          aria-label={`Voir la carte de ${citizen.displayName}`}
          onClick={() => setViewedId(citizen.userId)}
        >
          <Eye size={17} aria-hidden="true" />
        </button>
      </li>
    );
  };

  const memberHeading = (count, singular, plural) =>
    `${count === 1 ? "Membre" : "Membres"} ${count === 1 ? singular : plural}`;

  const renderGroup = (title, list, empty, tone) => (
    <section className="kingdom-citizens__group">
      <h3 className="kingdom-citizens__group-title">
        <span>
          {title} ({list.length})
        </span>
        <span
          className={`kingdom-citizens__dot kingdom-citizens__dot--${tone}`}
          aria-hidden="true"
        />
      </h3>
      {list.length ? (
        <ul className="kingdom-citizens__list">{list.map(renderCitizenRow)}</ul>
      ) : (
        <p className="kingdom-citizens__hint">{empty}</p>
      )}
    </section>
  );

  const cardCitizen = me;
  const cardBlason = citizenBlasonIndex(
    cardCitizen || { self: true, blasonIndex: 0 },
    selfName
  );
  const cardName = cardCitizen?.displayName || selfName || "Citoyen";

  return createPortal(
    <>
      <div className="kingdom-citizens">
        <MenuIconTooltip
          tooltip={FRAME_LAUNCHER_TOOLTIPS.citizens}
          placement="below"
          portal
          className="kingdom-citizens__tip"
          style={toggleStyle}
        >
          <button
            type="button"
            className="kingdom-citizens__toggle"
            aria-label={FRAME_LAUNCHER_TOOLTIPS.citizens}
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <ChairIcon className="kingdom-citizens__toggle-icon" aria-hidden="true" />
          </button>
        </MenuIconTooltip>
      </div>
      {open ? (
        <div
          className="delete-word-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            className="kingdom-citizens__window"
            style={citizensWindowStyle(
              frameBox,
              textScale > 1 ? TEXT_SCALE_FRAME_GROW : 0,
              textScale > 1 ? 100 : 0
            )}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kingdom-citizens-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="kingdom-citizens__ornament kingdom-citizens__ornament--nw" aria-hidden="true">
              <Arabesque />
            </div>
            <div className="kingdom-citizens__ornament kingdom-citizens__ornament--ne" aria-hidden="true">
              <Arabesque />
            </div>
            <div className="kingdom-citizens__ornament kingdom-citizens__ornament--sw" aria-hidden="true">
              <Arabesque />
            </div>
            <div className="kingdom-citizens__ornament kingdom-citizens__ornament--se" aria-hidden="true">
              <Arabesque />
            </div>
            <button
              type="button"
              className="delete-word-modal__close kingdom-citizens__close"
              onClick={close}
              aria-label="Fermer"
              disabled={saving}
            >
              <X size={18} strokeWidth={2} />
            </button>
            <div className="kingdom-citizens__tabs" role="tablist" aria-label="Annuaire du Royaume">
              <button
                type="button"
                id="kingdom-citizens-title"
                role="tab"
                aria-selected={tab === "directory"}
                className={`kingdom-citizens__tab${
                  tab === "directory" ? " kingdom-citizens__tab--active" : ""
                }`}
                onClick={() => {
                  setTab("directory");
                  setViewedId("");
                }}
              >
                <span className="kingdom-citizens__tab-label">Annuaire</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "card"}
                className={`kingdom-citizens__tab${
                  tab === "card" ? " kingdom-citizens__tab--active" : ""
                }`}
                onClick={() => setTab("card")}
              >
                <span className="kingdom-citizens__tab-label">Mon profil public</span>
              </button>
            </div>
            <div className="kingdom-citizens__body">
              {tab === "directory" ? (
                viewed ? (
                  <div className="kingdom-citizens__detail">
                    <button
                      type="button"
                      className="kingdom-citizens__back"
                      onClick={() => setViewedId("")}
                    >
                      <ChevronLeft size={16} aria-hidden="true" />
                      Retour à l&apos;annuaire
                    </button>
                    <div className="kingdom-citizens__identity kingdom-citizens__identity--detail">
                      <img
                        className="kingdom-citizens__portrait"
                        src={blasonSrc(citizenBlasonIndex(viewed, selfName))}
                        alt=""
                      />
                      <div className="kingdom-citizens__identity-copy">
                        <p className="kingdom-citizens__identity-name">{viewed.displayName}</p>
                        <LiteraryQuote text={viewed.presentation} />
                      </div>
                    </div>
                    <CitizensDivider />
                    <dl className="kingdom-citizens__facts">
                      <div>
                        <dt>{NOVEL_LABEL}</dt>
                        <dd className={viewed.favoriteNovel ? "" : "kingdom-citizens__empty-value"}>
                          {viewed.favoriteNovel || "Non renseigné"}
                        </dd>
                      </div>
                      <div>
                        <dt>{CHARACTER_LABEL}</dt>
                        <dd
                          className={
                            viewed.inspiringCharacter ? "" : "kingdom-citizens__empty-value"
                          }
                        >
                          {viewed.inspiringCharacter || "Non renseigné"}
                        </dd>
                      </div>
                      <div>
                        <dt>{READING_LABEL}</dt>
                        <dd className={viewed.currentReading ? "" : "kingdom-citizens__empty-value"}>
                          {viewed.currentReading || "Non renseigné"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <div className="kingdom-citizens__scroll">
                    <div
                      className="kingdom-citizens__scroll-body"
                      ref={listRef}
                      onScroll={updateScrollbar}
                    >
                      {loading && !citizens.length ? <DirectorySkeleton /> : null}
                      {error ? <p className="kingdom-citizens__error">{error}</p> : null}
                      {!loading || citizens.length ? (
                        <>
                          {renderGroup(
                            memberHeading(
                              online.length,
                              "actuellement connecté",
                              "actuellement connectés"
                            ),
                            online,
                            "Personne n'est connecté actuellement.",
                            "online"
                          )}
                          <CitizensDivider />
                          {renderGroup(
                            memberHeading(offline.length, "hors ligne", "hors ligne"),
                            offline,
                            "Aucun citoyen n'est hors ligne.",
                            "offline"
                          )}
                        </>
                      ) : null}
                    </div>
                    <CitizensScrollbar
                      trackRef={trackRef}
                      scrollbar={scrollbar}
                      onTrackPointerDown={handleTrackPointerDown}
                      onThumbPointerDown={handleThumbPointerDown}
                    />
                  </div>
                )
              ) : (
                <div className="kingdom-citizens__card">
                  <div className="kingdom-citizens__identity">
                    <img className="kingdom-citizens__portrait" src={blasonSrc(cardBlason)} alt="" />
                    <div className="kingdom-citizens__identity-copy">
                      <p className="kingdom-citizens__identity-name">{cardName}</p>
                      <div className="kingdom-citizens__mode">
                        <span className="kingdom-citizens__mode-mark">
                          {editing ? (
                            "Modifier"
                          ) : (
                            <Eye size={17} aria-hidden="true" />
                          )}
                        </span>
                        <ToggleSwitch
                          checked={editing}
                          onChange={finishEditing}
                          disabled={saving || loading}
                          plain
                          trackOffColor="#39210d"
                          trackOnColor="#f0a020"
                          ariaLabel={
                            editing
                              ? "Enregistrer la carte de citoyenneté"
                              : "Modifier la carte de citoyenneté"
                          }
                          compact
                        />
                      </div>
                    </div>
                  </div>
                  <LiteraryLine
                    label={NOVEL_LABEL}
                    tip={LINE_TIP}
                    value={draft.favoriteNovel}
                    editing={editing}
                    field="favoriteNovel"
                    draft={draft}
                    onDraft={updateDraft}
                  />
                  <LiteraryLine
                    label={CHARACTER_LABEL}
                    tip={LINE_TIP}
                    value={draft.inspiringCharacter}
                    editing={editing}
                    field="inspiringCharacter"
                    draft={draft}
                    onDraft={updateDraft}
                  />
                  <LiteraryLine
                    label={READING_LABEL}
                    tip={LINE_TIP}
                    value={draft.currentReading}
                    editing={editing}
                    field="currentReading"
                    draft={draft}
                    onDraft={updateDraft}
                  />
                  <LiteraryLine
                    label={PRESENTATION_LABEL}
                    tip={PRESENTATION_TIP}
                    value={draft.presentation}
                    editing={editing}
                    field="presentation"
                    draft={draft}
                    onDraft={updateDraft}
                    multiline
                  />
                  {editing ? (
                    <p className="kingdom-citizens__save-hint">
                      cliquez sur le bouton orange pour enregistrer
                    </p>
                  ) : null}
                  <CitizensDivider />
                  {cardError ? <p className="kingdom-citizens__error">{cardError}</p> : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>,
    portalRoot
  );
}
